import React, { useEffect, useState } from 'react';
import {
    Settings, ShieldCheck, Briefcase, Users, 
    Check, X, ChevronDown, UserCircle
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { mockUsers } from '../../dummy/dummy';

// --- HELPER: AVATAR COMPONENT ---
// Provides a premium fallback if the user has no avatar image
const UserAvatar = ({ user, size = 'w-14 h-14' }) => {
    if (user?.avatarUrl) {
        return (
            <img 
                src={user.avatarUrl} 
                alt={user.name} 
                className={`${size} rounded-full object-cover ring-4 ring-white shadow-md`} 
            />
        );
    }
    return (
        <div className={`${size} rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center ring-4 ring-white shadow-md`}>
            <span className="text-lg font-black text-slate-500 uppercase tracking-widest">
                {user?.name?.charAt(0) || <UserCircle size={24} />}
            </span>
        </div>
    );
};


// --- MAIN COMPONENT ---
const TeamView = () => {
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [pageIsLoading, setPageIsLoading] = useState(false);
    const users = mockUsers;
    
    const [project, setProject] = useState(null);    
    const [projectId, setProjectId] = useState(localStorage.getItem("selectedProject"));

    // Fallbacks in case project data is missing
    const currentAuditor = users?.find(u => u.id === project?.auditorId) || users?.[0] || {};
    const currentCoordinator = users?.find(u => u.id === project?.coordinatorId) || users?.[1] || {};

    const [selectedAuditor, setSelectedAuditor] = useState(currentAuditor);
    const [selectedCoordinator, setSelectedCoordinator] = useState(currentCoordinator);
    
    
    const dispatch = useDispatch();
    const { switched } = useSelector((state => state?.rootReducer?.project || {}));
    const { currentUser } = useSelector((state => state?.rootReducer?.users || {}));

    
    useEffect(() => {
        if(!switched?.projectId) return;
        setProjectId(switched?.projectId);  
        setProject(switched);
    }, [switched]);

    const onUpdateProject = ({auditorId, coordinatorId}) => {}

    const handleSave = () => {
        setIsSaving(true);
        setPageIsLoading(true);
        
        // Simulating API call
        setTimeout(() => {
            onUpdateProject({
                auditorId: selectedAuditor.id,
                coordinatorId: selectedCoordinator.id
            });
            setIsEditing(false);
            setIsSaving(false);
            setPageIsLoading(false);
        }, 600);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setSelectedAuditor(currentAuditor);
        setSelectedCoordinator(currentCoordinator);
    };

    return (
        <div className="animate-in fade-in zoom-in-[0.98] duration-400 ease-out  mx-auto">
            
            <div className="bg-slate-50  relative">
                {/* --- HEADER --- */}
                <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-indigo-600 rounded-2xl shadow-lg shadow-indigo-600/20 text-white">
                            <Users size={28} strokeWidth={2} />
                        </div>
                        <div>
                            <h3 className="text-2xl font-black text-slate-900 tracking-tight">Project Team</h3>
                            <p className="text-sm font-semibold text-slate-500 mt-0.5">Manage the key stakeholders assigned to this compliance project.</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            className="flex gap-2 items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all shadow-md active:scale-95"
                            onClick={() => setIsEditing(true)}>
                            <Settings size={18} />
                            Edit Assignments
                        </button>
                    </div>
                </div>

                {/* --- CONTENT --- */}
                <div className="relative z-10 ">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        
                        {/* 1. Primary Auditor Card */}
                        <div className={`relative p-6 sm:p-8 rounded-3xl transition-all duration-300 ${isEditing ? 'bg-indigo-50/50 ring-1 ring-indigo-200 shadow-inner' : 'bg-white ring-1 ring-slate-200/60 shadow-sm hover:shadow-md'}`}>
                            {/* Role Badge */}
                            <div className="flex items-center gap-2.5 mb-8">
                                <div className="p-2 bg-indigo-100 text-indigo-600 rounded-lg">
                                    <ShieldCheck size={18} strokeWidth={2.5} />
                                </div>
                                <span className="font-black uppercase tracking-widest text-xs text-indigo-900">Primary Auditor</span>
                            </div>

                            {isEditing ? (
                                <div className="space-y-3 animate-in fade-in duration-300">
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">Select Auditor</label>
                                    <div className="relative group">
                                        <select
                                            className="w-full appearance-none bg-white p-4 pr-12 rounded-xl ring-1 ring-slate-200 focus:ring-2 focus:ring-indigo-500 focus:outline-none transition-all font-bold text-slate-700 shadow-sm cursor-pointer group-hover:ring-indigo-300"
                                            value={selectedAuditor?.id || ''}
                                            onChange={(e) => setSelectedAuditor(users.find(u => u.id === e.target.value))}
                                        >
                                            {users?.map(u => (
                                                <option key={u.id} value={u.id}>{u.name} — {u.role}</option>
                                            ))}
                                        </select>
                                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5 pointer-events-none group-hover:text-indigo-500 transition-colors" />
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-center gap-5 animate-in fade-in duration-300">
                                    <UserAvatar user={currentAuditor} />
                                    <div>
                                        <p className="text-xl font-extrabold text-slate-900">{currentAuditor?.name}</p>
                                        <p className="text-sm font-semibold text-slate-500 mt-0.5">{currentAuditor?.email}</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* 2. Project Coordinator Card */}
                        <div className={`relative p-6 sm:p-8 rounded-3xl transition-all duration-300 ${isEditing ? 'bg-emerald-50/50 ring-1 ring-emerald-200 shadow-inner' : 'bg-white ring-1 ring-slate-200/60 shadow-sm hover:shadow-md'}`}>
                            {/* Role Badge */}
                            <div className="flex items-center gap-2.5 mb-8">
                                <div className="p-2 bg-emerald-100 text-emerald-600 rounded-lg">
                                    <Briefcase size={18} strokeWidth={2.5} />
                                </div>
                                <span className="font-black uppercase tracking-widest text-xs text-emerald-900">Project Coordinator</span>
                            </div>

                            {isEditing ? (
                                <div className="space-y-3 animate-in fade-in duration-300">
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">Select Coordinator</label>
                                    <div className="relative group">
                                        <select
                                            className="w-full appearance-none bg-white p-4 pr-12 rounded-xl ring-1 ring-slate-200 focus:ring-2 focus:ring-emerald-500 focus:outline-none transition-all font-bold text-slate-700 shadow-sm cursor-pointer group-hover:ring-emerald-300"
                                            value={selectedCoordinator?.id || ''}
                                            onChange={(e) => setSelectedCoordinator(users.find(u => u.id === e.target.value))}
                                        >
                                            {users?.map(u => (
                                                <option key={u.id} value={u.id}>{u.name} — {u.role}</option>
                                            ))}
                                        </select>
                                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5 pointer-events-none group-hover:text-emerald-500 transition-colors" />
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-center gap-5 animate-in fade-in duration-300">
                                    <UserAvatar user={currentCoordinator} />
                                    <div>
                                        <p className="text-xl font-extrabold text-slate-900">{currentCoordinator.name}</p>
                                        <p className="text-sm font-semibold text-slate-500 mt-0.5">{currentCoordinator.email}</p>
                                    </div>
                                </div>
                            )}
                        </div>

                    </div>

                    {/* --- EDIT ACTIONS FOOTER --- */}
                    {isEditing && (
                        <div className="mt-8 flex flex-col sm:flex-row justify-end items-center gap-3 pt-8 border-t border-slate-200/60 animate-in fade-in slide-in-from-bottom-2 duration-300">
                            <button
                                onClick={handleCancel}
                                disabled={isSaving}
                                className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 text-sm font-bold text-slate-600 bg-white ring-1 ring-slate-200 hover:bg-slate-50 hover:text-slate-900 rounded-xl transition-all shadow-sm disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-800 rounded-xl shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all active:scale-95 disabled:opacity-70 disabled:pointer-events-none"
                            >
                                {isSaving ? (
                                    <div className="w-5 h-5 border-2 border-slate-400 border-t-white rounded-full animate-spin"></div>
                                ) : (
                                    <Check size={18} className="text-emerald-400" />
                                )}
                                {isSaving ? 'Saving...' : 'Save Assignments'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TeamView;